import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { UpgradeLockCard } from '../components/UpgradeLock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { LifeBuoy, Plus, Loader2, Paperclip, Image as ImageIcon, X, Send, User, ArrowLeft } from 'lucide-react';
import AppFooter from './../components/AppFooter';

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-500',
};
const PRIORITY_COLORS = { low: 'text-slate-400', medium: 'text-amber-500', high: 'text-red-500' };

// Only owners/admins/PMs can access this page — rule from the product spec.
const ALLOWED_ROLES = ['owner', 'admin', 'project_manager'];

export default function ContactSupportPage() {
  const { currentOrg, getOrgHeaders, API } = useAuth();
  const { hasCap, loading: subLoading } = useSubscription();
  const role = currentOrg?.role;
  const canCreate = ALLOWED_ROLES.includes(role);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/tickets`, { withCredentials: true, headers: getOrgHeaders() });
      setTickets(r.data);
    } catch (err) {
      if (err.response?.status !== 403 && err.response?.status !== 402) toast.error('Failed to load tickets');
    } finally { setLoading(false); }
  }, [API, getOrgHeaders]);

  useEffect(() => { if (currentOrg && hasCap('contact_support')) fetchTickets(); else setLoading(false); }, [currentOrg, hasCap, fetchTickets]);

  // Free plan — show upgrade card instead of the feature.
  if (!subLoading && !hasCap('contact_support')) {
    return (
      <UpgradeLockCard
        title="Contact Support is a Business plan feature"
        description="Upgrade to raise tickets with the Inspiro Projects support team and get image attachments, status tracking, and email notifications."
        featureName="contact_support"
        testId="support-upgrade-lock"
      />
    );
  }

  if (!canCreate && tickets.length === 0 && !loading) {
    return (
      <div className="flex-1 overflow-y-auto p-8" data-testid="contact-support-page">
        <div className="max-w-xl mx-auto text-center pt-20">
          <LifeBuoy className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h1 className="text-2xl font-semibold text-slate-900">Contact Support</h1>
          <p className="text-sm text-slate-500 mt-2">
            Please ask your workspace owner, admin, or project manager to raise a support ticket on your behalf.
          </p>
        </div>
      </div>
    );
  }

  if (selected) {
    return (
      <TicketDetail
        ticket={selected}
        onBack={() => { setSelected(null); fetchTickets(); }}
        canCreate={canCreate}
      />
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-slate-50" data-testid="contact-support-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Contact Support</h1>
              <p className="text-xs text-slate-500">Tickets raised by your workspace</p>
            </div>
          </div>
          {canCreate && (
            <Button
              onClick={() => setShowCreate(true)}
              className="h-9 rounded-xl bg-slate-900 hover:bg-slate-800"
              data-testid="support-new-ticket-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Ticket
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : tickets.length === 0 ? (
          <Card className="border-slate-200/60">
            <CardContent className="py-16 text-center">
              <LifeBuoy className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500">No tickets yet.</p>
              {canCreate && <p className="text-xs text-slate-400 mt-1">Click "New Ticket" to raise your first support request.</p>}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="support-ticket-list">
            {tickets.map(t => (
              <button
                key={t.ticket_id}
                onClick={() => setSelected(t)}
                className="w-full text-left bg-white rounded-xl border border-slate-200/70 hover:border-slate-300 p-4 transition-colors"
                data-testid={`support-ticket-${t.ticket_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{t.title}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-slate-400">
                      <span>{t.ticket_id}</span>
                      <span>·</span>
                      <span>{new Date(t.created_at).toLocaleDateString()}</span>
                      {t.replies?.length > 0 && <><span>·</span><span>{t.replies.length} {t.replies.length === 1 ? 'reply' : 'replies'}</span></>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge className={`${STATUS_COLORS[t.status] || STATUS_COLORS.open} text-[10px]`}>{t.status.replace('_', ' ')}</Badge>
                    <span className={`text-[10px] font-medium uppercase ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchTickets(); }}
        />
      )}
      <AppFooter />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CREATE TICKET MODAL
// ═══════════════════════════════════════════════════════════════
function CreateTicketModal({ onClose, onCreated }) {
  const { getOrgHeaders, API } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('general');
  const [attachments, setAttachments] = useState([]); // [{url, filename}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) { toast.error(`${file.name} is not an image`); continue; }
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { ...getOrgHeaders() } });
        setAttachments(prev => [...prev, { url: r.data.url, filename: file.name }]);
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const submit = async () => {
    if (!title.trim() || !description.trim()) { toast.error('Title and description are required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`,
        { title, description, priority, category, attachments },
        { withCredentials: true, headers: getOrgHeaders() }
      );
      toast.success('Ticket created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="support-new-ticket-modal">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">New Support Ticket</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <Label className="text-xs text-slate-500">Subject</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Short description..." className="mt-1 h-9 text-sm" data-testid="support-ticket-title" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} rows={5}
              placeholder="Describe the problem in detail..." className="mt-1 text-sm" data-testid="support-ticket-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Priority</Label>
              <select value={priority} onChange={e => setPriority(e.target.value)} className="mt-1 h-9 px-2 text-sm rounded-md border border-slate-200 bg-white w-full" data-testid="support-ticket-priority">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Category</Label>
              <select value={category} onChange={e => setCategory(e.target.value)} className="mt-1 h-9 px-2 text-sm rounded-md border border-slate-200 bg-white w-full" data-testid="support-ticket-category">
                <option value="general">General</option>
                <option value="billing">Billing</option>
                <option value="bug">Bug</option>
                <option value="feature_request">Feature Request</option>
                <option value="integration">Integration</option>
              </select>
            </div>
          </div>

          {/* Attachments */}
          <div>
            <Label className="text-xs text-slate-500">Attachments (images only)</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              {attachments.map((a, i) => (
                <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-slate-200" data-testid={`support-attachment-${i}`}>
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))} className="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full p-0.5">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              ))}
              <label className="flex flex-col items-center justify-center w-16 h-16 rounded-lg border-2 border-dashed border-slate-200 text-slate-400 hover:border-slate-400 hover:text-slate-600 cursor-pointer">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="support-ticket-upload" />
              </label>
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="h-9 rounded-xl">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="h-9 rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="support-ticket-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TICKET DETAIL
// ═══════════════════════════════════════════════════════════════
function TicketDetail({ ticket: initial, onBack, canCreate }) {
  const { getOrgHeaders, API } = useAuth();
  const [ticket, setTicket] = useState(initial);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/support/tickets/${ticket.ticket_id}/reply`,
        { message: replyText },
        { withCredentials: true, headers: getOrgHeaders() }
      );
      setTicket(r.data);
      setReplyText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally { setSending(false); }
  };

  return (
    <div className="flex-1 overflow-y-auto p-6 bg-slate-50" data-testid="support-ticket-detail">
      <div className="max-w-3xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900 mb-4" data-testid="support-ticket-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All Tickets
        </button>

        <Card className="border-slate-200/60">
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base">{ticket.title}</CardTitle>
                <p className="text-[11px] text-slate-400 mt-1 font-mono">{ticket.ticket_id}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={STATUS_COLORS[ticket.status]}>{ticket.status.replace('_', ' ')}</Badge>
                <span className={`text-xs font-medium uppercase ${PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1 text-[11px] text-slate-500">
                <User className="w-3 h-3" /> You
                <span className="text-slate-300">·</span>
                <span>{new Date(ticket.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm text-slate-900 whitespace-pre-wrap">{ticket.description}</p>
              {ticket.attachments?.length > 0 && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {ticket.attachments.map((a, i) => (
                    <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-slate-200 block">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} alt="" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </div>
            {(ticket.replies || []).map(r => (
              <div key={r.id} className={`rounded-xl p-3 ${r.sender_type === 'admin' ? 'bg-violet-50' : 'bg-slate-50'}`}>
                <div className="flex items-center gap-2 mb-1 text-[11px] text-slate-500">
                  {r.sender_type === 'admin' ? <LifeBuoy className="w-3 h-3 text-violet-600" /> : <User className="w-3 h-3" />}
                  <span className={r.sender_type === 'admin' ? 'text-violet-700 font-medium' : 'text-slate-700 font-medium'}>{r.sender_type === 'admin' ? 'Inspiro Support' : r.sender_name}</span>
                  <span className="text-slate-300">·</span>
                  <span>{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <p className="text-sm text-slate-900 whitespace-pre-wrap">{r.message}</p>
              </div>
            ))}

            {canCreate && ticket.status !== 'closed' && (
              <form onSubmit={e => { e.preventDefault(); sendReply(); }} className="pt-2 flex gap-2">
                <Input value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Reply..." className="h-9 rounded-xl" data-testid="support-ticket-reply-input" />
                <Button type="submit" disabled={!replyText.trim() || sending} className="h-9 rounded-xl" data-testid="support-ticket-reply-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
